// @/services/content/store/base.js

/**
 * BaseContentStore
 * 
 * Provides core functionality for content management:
 * - Multi-locale content structure with fallback mechanisms
 * - Subscription system for reactive components
 * - GraphQL data preloading during SSR/SSG
 * - Comprehensive content caching for performance
 * - Module-specific content organization
 * - Smart content mapping with locale fallbacks
 */
export class BaseContentStore {
  /**
   * Initialize the base content store
   * Creates the core state structure for all content types with locale support
   */
  constructor() {
    // Initialize core state structure
    this.state = {
      initialized: false,
      data: {
        carbonAccounting: { en: null },
        carbonAccountingUpdate: { en: null },
        carbonAccountingAddDrawer: { en: null },
        carbonAccountingDistributionDrawer: { en: null },
        carbonAccountingResults: { en: null },
        carbonAccountingCharts: { en: null },
        supplyChainManagement: {
          en: null, // Default locale content for SCM
        },
        scmInvite: {
          en: null, // Default locale content for SCM Invite
        },
        seBatchInvite: {
          en: null, // Default locale content for SE Batch Invite Dialog
        },
        scmTrial: {
          en: null, // Default locale content for SCM Trial Mode
        },
        scmSupplierDetails: {
          en: null, // Default locale content for SCM Supplier Details
        },
        scmReminderDialog: {
          en: null, // Default locale content for SCM Reminder Dialog
        },
        scmExport: {
          en: null, // Default locale content for SCM Export
        },
        hotspotSectors: {
          en: null, // Default locale content for Hotspot Sectors (primary source is the useContentHotspotSectors GraphQL query; this store slot backs the hook's fallback path)
        },
        selfAssessment: {
          en: null, // Default locale content for Self Assessment
        },
        selfAssessmentBS: {
          en: null, // Default locale content
        },
        selfAssessmentCO: {
          en: null, // Default locale content
        },
        selfAssessmentBSSection: {
          en: null, // Default locale content
        },
        selfAssessmentBSQuestions: {
          en: null, // Default locale content
        },
        selfAssessmentBSELearning: {
          en: null, // Default locale content for eLearning
        },
        firstRun: {
          en: null, // Default locale content for First Run
        },
        customerRequests: {
          en: null, // Default locale content for Customer Requests
        },
        sidebar: {
          en: null, // Default locale content for Sidebar Navigation
        },
        submission: {
          en: null, // Default locale content for Submission Drawer
        },
        users: {
          en: null, // Default locale content for User Management
        },
        settings: {
          en: null, // Default locale content for Account Settings
        },
        companyProfile: {
          en: null, // Default locale content for Company Profile
        },
        registration: {
          en: null, // Default locale content for Registration
        },
        invitation: {
          en: null, // Default locale content for Invitation
        },
        industryList: {
          en: null, // Default locale content for Industry List
        }
      },
    };

    this.subscribers = new Map();
  }

  /**
   * Subscribe to content changes
   * Registers a callback function to be notified when specific content changes
   * 
   * @param {string} type - Type of content to subscribe to
   * @param {Function} callback - Callback function to be called when content changes
   * @returns {Function} Unsubscribe function to remove the subscription
   */
  subscribe(type, callback) {
    if (!this.subscribers.has(type)) {
      this.subscribers.set(type, new Set());
    }
    this.subscribers.get(type).add(callback);
    return () => this.subscribers.get(type).delete(callback);
  }

  /**
   * Notify subscribers of content changes
   * Triggers callbacks for all subscribers to a specific content type
   * 
   * @param {string} type - Type of content that changed
   * @param {*} data - Optional data to pass to subscribers
   */
  notifySubscribers(type, data = null) {
    const subscribers = this.subscribers.get(type);
    if (subscribers) {
      subscribers.forEach(callback => callback(data || this.state.data[type]));
    }
  }

  /**
   * Preload content from Gatsby's GraphQL data layer
   * Loads content during SSR/SSG for optimal performance
   * 
   * @returns {boolean} True if data was loaded successfully
   */
  preloadFromGraphQL() {
    if (typeof window === 'undefined' || !window.__GATSBY_INITIAL_DATA__) {
      return false;
    }

    try {
      // Check all static queries for our content
      if (window.__GATSBY_INITIAL_DATA__.staticQueryResults) {
        const staticQueries = window.__GATSBY_INITIAL_DATA__.staticQueryResults;
        let contentFound = false;

        // Look through all queries
        for (const queryId in staticQueries) {
          const queryResult = staticQueries[queryId];

          // Check for First Run content
          if (queryResult?.data?.allContentFirstRun?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentFirstRun.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.firstRun[locale]) {
                this.state.data.firstRun[locale] = {};
              }

              // Store all properties
              this.state.data.firstRun[locale] = {
                pageTitle: node.pageTitle,
                pageSubtitle: node.pageSubtitle,
                progressLabel: node.progressLabel,
                backButtonText: node.backButtonText,
                continueButtonText: node.continueButtonText,
                submitButtonText: node.submitButtonText,
                reductionTargetsStepName: node.reductionTargetsStepName,
                businessSegmentsStepName: node.businessSegmentsStepName,
                carbonFootprintStepName: node.carbonFootprintStepName,
                cdpInsightsStepName: node.cdpInsightsStepName,
                carbonReductionIntroTitle: node.carbonReductionIntroTitle,
                carbonReductionIntroText: node.carbonReductionIntroText,
                carbonReductionIntroButtonText: node.carbonReductionIntroButtonText,
                multiSegmentIntroTitle: node.multiSegmentIntroTitle,
                multiSegmentIntroText: node.multiSegmentIntroText,
                targetsSuccessTitle: node.targetsSuccessTitle,
                targetsSuccessText: node.targetsSuccessText,
                targetsSuccessContinueText: node.targetsSuccessContinueText,
                segmentsSuccessTitle: node.segmentsSuccessTitle,
                segmentsSuccessText: node.segmentsSuccessText,
                segmentsSuccessContinueText: node.segmentsSuccessContinueText,
                insightsSuccessTitle: node.insightsSuccessTitle,
                insightsSuccessText: node.insightsSuccessText,
                insightsSuccessSubmitText: node.insightsSuccessSubmitText,
                welcomeDialogTitle: node.welcomeDialogTitle,
                welcomeDialogText: node.welcomeDialogText,
                welcomeDialogButtonText: node.welcomeDialogButtonText,
                successDialogTitle: node.successDialogTitle,
                successDialogText: node.successDialogText,
                successDialogButtonText: node.successDialogButtonText,
                questionnaireTitle: node.questionnaireTitle,
                questionnaireSubtitle: node.questionnaireSubtitle,
                locale: node.locale
              };
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded First Run content for locales:',
                Object.keys(this.state.data.firstRun));
            }
            contentFound = true;
          }

          // Check for Carbon Accounting content
          if (queryResult?.data?.allContentCa?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentCa.nodes;

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              if (!this.state.data.allContentCa[locale]) {
                this.state.data.allContentCa[locale] = {};
              }

              // Store all properties
              this.state.data.allContentCa[locale] = {
                // Map all fields from node
                locale: node.locale
              };
            });
            contentFound = true;
          }

          // Carbon Accounting Update content
          if (queryResult?.data?.allContentCaUpdate?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentCaUpdate.nodes;

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              if (!this.state.data.allContentCaUpdate[locale]) {
                this.state.data.allContentCaUpdate[locale] = {};
              }

              // Store all properties
              this.state.data.allContentCaUpdate[locale] = {
                // Map all fields from node
                locale: node.locale
              };
            });
            contentFound = true;
          }

          // Carbon Accounting Add Drawer content
          if (queryResult?.data?.allContentCaAddDrawer?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentCaAddDrawer.nodes;

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              if (!this.state.data.carbonAccountingAddDrawer[locale]) {
                this.state.data.carbonAccountingAddDrawer[locale] = {};
              }

              // Store all properties
              this.state.data.carbonAccountingAddDrawer[locale] = {
                // Map all fields from node
                locale: node.locale
              };
            });
            contentFound = true;
          }

          // Carbon Accounting Distribution Drawer content
          if (queryResult?.data?.allContentCaDistributionDrawer?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentCaDistributionDrawer.nodes;

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              if (!this.state.data.carbonAccountingDistributionDrawer[locale]) {
                this.state.data.carbonAccountingDistributionDrawer[locale] = {};
              }

              // Store all properties
              this.state.data.carbonAccountingDistributionDrawer[locale] = {
                // Map all fields from node
                locale: node.locale
              };
            });
            contentFound = true;
          }

          // Carbon Accounting Charts content
          if (queryResult?.data?.allContentCaCharts?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentCaCharts.nodes;

            if (process.env.NODE_ENV === 'development') {
              console.log('CHARTS DEBUG - Found nodes:', nodes.length);
            }

            // Initialize the charts content structure if it doesn't exist
            if (!this.state.data.carbonAccountingCharts) {
              this.state.data.carbonAccountingCharts = {};
            }

            nodes.forEach(node => {
              // Extract locale with a fallback to 'en'
              const locale = node.locale || 'en';

              // Initialize locale container if needed
              if (!this.state.data.carbonAccountingCharts[locale]) {
                this.state.data.carbonAccountingCharts[locale] = {};
              }

              // Log the node structure for better debugging
              if (process.env.NODE_ENV === 'development' && nodes.indexOf(node) === 0) {
                console.log('CHARTS DEBUG - First node sample fields:',
                  Object.entries(node)
                    .filter(([key]) => !['id', 'internal', 'children', 'parent'].includes(key))
                    .slice(0, 5)
                    .map(([key, value]) => `${key}: "${value}"`)
                );
              }

              // Copy all content fields EXCEPT internal Gatsby fields and metadata
              // This is the key fix - directly copy all content fields
              for (const key in node) {
                // Skip internal Gatsby fields and empty values
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  if (node[key] !== undefined && node[key] !== null) {
                    this.state.data.carbonAccountingCharts[locale][key] = node[key];
                  }
                }
              }
            });

            // Log what was loaded for debugging
            if (process.env.NODE_ENV === 'development') {
              console.log('CHARTS DEBUG - Content store after loading:',
                Object.keys(this.state.data.carbonAccountingCharts).map(locale => ({
                  locale,
                  fieldCount: Object.keys(this.state.data.carbonAccountingCharts[locale] || {}).length,
                  sampleFields: Object.keys(this.state.data.carbonAccountingCharts[locale] || {}).slice(0, 3)
                }))
              );
            }

            contentFound = true;
          }

          // Carbon Accounting Results content
          if (queryResult?.data?.allContentCaResults?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentCaResults.nodes;

            // Debug: Log the first node to see all available fields
            if (process.env.NODE_ENV === 'development' && nodes.length > 0) {
              console.log("CONTENT DEBUG - Results Fields:", Object.keys(nodes[0]));
            }

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              if (!this.state.data.carbonAccountingResults[locale]) {
                this.state.data.carbonAccountingResults[locale] = {};
              }

              // Copy ALL fields directly from the node, not just locale
              this.state.data.carbonAccountingResults[locale] = { ...node };
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Carbon Accounting Results content for locales:',
                Object.keys(this.state.data.carbonAccountingResults));
            }
            contentFound = true;
          }

          if (queryResult?.data?.allContentScm?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentScm.nodes;

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.allContentScm[locale]) {
                this.state.data.allContentScm[locale] = {};
              }

              // Copy all fields directly from the node - instead of manually listing fields
              for (const key in node) {
                // Skip internal Gatsby fields
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  this.state.data.allContentScm[locale][key] = node[key];
                }
              }
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded SCM content for locales:',
                Object.keys(this.state.data.allContentScm));
            }
            contentFound = true;
          }

          // Check for SCM Invite Drawer content
          if (queryResult?.data?.allContentScmInvite?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentScmInvite.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.scmInvite[locale]) {
                this.state.data.scmInvite[locale] = {};
              }

              // Copy all fields directly from the node - make sure this approach is used
              // instead of manually listing fields
              for (const key in node) {
                // Skip internal Gatsby fields
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  this.state.data.scmInvite[locale][key] = node[key];
                }
              }
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded SCM Invite Drawer content for locales:',
                Object.keys(this.state.data.scmInvite));
            }
            contentFound = true;
          }

          // Check for SCM Trial Mode content
          if (queryResult?.data?.allContentScmTrial?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentScmTrial.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.scmTrial[locale]) {
                this.state.data.scmTrial[locale] = {};
              }

              // Copy all fields directly from the node - make sure this approach is used
              // instead of manually listing fields
              for (const key in node) {
                // Skip internal Gatsby fields
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  this.state.data.scmTrial[locale][key] = node[key];
                }
              }
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded SCM Trial Mode content for locales:',
                Object.keys(this.state.data.scmTrial));
            }
            contentFound = true;
          }

          // Check for SCM Supplier Details content
          if (queryResult?.data?.allContentScmSp?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentScmSp.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.scmSupplierDetails[locale]) {
                this.state.data.scmSupplierDetails[locale] = {};
              }

              // Store all properties
              this.state.data.scmSupplierDetails[locale] = {
                drawerTitle: node.drawerTitle,
                closeButtonText: node.closeButtonText,
                requestUpdateButtonText: node.requestUpdateButtonText,
                requestSubmissionButtonText: node.requestSubmissionButtonText,
                viewButtonText: node.viewButtonText,
                loadingMessage: node.loadingMessage,
                successMessage: node.successMessage,

                // Supplier info section
                supplierNameIdLabel: node.supplierNameIdLabel,
                supplierStatusLabel: node.supplierStatusLabel,
                industrySectorLabel: node.industrySectorLabel,
                countryLabel: node.countryLabel,
                contactNameLabel: node.contactNameLabel,
                primaryEmailLabel: node.primaryEmailLabel,
                invitedEmailLabel: node.invitedEmailLabel,
                ccLabel: node.ccLabel,
                bccLabel: node.bccLabel,
                showCcBccLabel: node.showCcBccLabel,
                hideCcBccLabel: node.hideCcBccLabel,
                emailLanguageLabel: node.emailLanguageLabel,
                invitationCreatedLabel: node.invitationCreatedLabel,
                statusLastChangedLabel: node.statusLastChangedLabel,
                lastSubmissionLabel: node.lastSubmissionLabel,
                lastReminderSentLabel: node.lastReminderSentLabel,

                // Status messages
                invitedStatusText: node.invitedStatusText,
                acceptedStatusText: node.acceptedStatusText,
                declinedStatusText: node.declinedStatusText,
                submittedStatusText: node.submittedStatusText,

                // Implementation degree section
                implementationDegreeTitle: node.implementationDegreeTitle,
                measureStartYearLabel: node.measureStartYearLabel,
                todayLabel: node.todayLabel,
                oneYearLabel: node.oneYearLabel,
                threeYearLabel: node.threeYearLabel,
                supplyChainActivatedTitle: node.supplyChainActivatedTitle,
                supplyChainActivatedSubtitle: node.supplyChainActivatedSubtitle,
                supplyChainNotActivatedTitle: node.supplyChainNotActivatedTitle,
                supplyChainNotActivatedSubtitle: node.supplyChainNotActivatedSubtitle,
                notAvailable: node.notAvailable,
                plausibilityCheckTitle: node.plausibilityCheckTitle,
                plausibilityCheckSubtitle: node.plausibilityCheckSubtitle,
                noImplementationDataMessage: node.noImplementationDataMessage,
                measureNoAnswerMessage: node.measureNoAnswerMessage,
                measureNotApplicableMessage: node.measureNotApplicableMessage,
                hotspotNotApplicableMessage: node.hotspotNotApplicableMessage,
                renewableEnergyLabel: node.renewableEnergyLabel,
                energyEfficiencyLabel: node.energyEfficiencyLabel,
                logisticsLabel: node.logisticsLabel,
                recycledInputsLabel: node.recycledInputsLabel,
                emergingTechLabel: node.emergingTechLabel,

                // Reduction targets section
                reductionTargetsTitle: node.reductionTargetsTitle,
                scope12Label: node.scope12Label,
                scope3Label: node.scope3Label,
                reductionLabel: node.reductionLabel,
                sbtiVerifiedLabel: node.sbtiVerifiedLabel,
                allowsCompensationLabel: node.allowsCompensationLabel,
                noTargetDefinedMessage: node.noTargetDefinedMessage,
                noTargetsAvailableMessage: node.noTargetsAvailableMessage,
                reductionTargetTemplate: node.reductionTargetTemplate,

                // Carbon footprint section
                carbonFootprintTitle: node.carbonFootprintTitle,
                scope1Label: node.scope1Label,
                scope2Label: node.scope2Label,
                scope3UpstreamLabel: node.scope3UpstreamLabel,
                totalLabel: node.totalLabel,
                carbonChartYearLabel: node.carbonChartYearLabel,
                carbonChartEmissionsLabel: node.carbonChartEmissionsLabel,
                turnoverLabel: node.turnoverLabel,
                noCarbonFootprintDataMessage: node.noCarbonFootprintDataMessage,
                noFootprintKnownMessage: node.noFootprintKnownMessage,

                // Error & misc messages
                noDataLabel: node.noDataLabel,
                requestReminderErrorMessage: node.requestReminderErrorMessage,
                topOneHotspotLabel: node.topOneHotspotLabel,
                topTwoHotspotLabel: node.topTwoHotspotLabel,

                locale: node.locale
              };
            });

            contentFound = true;
          }

          // Check for SCM Reminder Dialog content
          if (queryResult?.data?.allContentScmRd?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentScmRd.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.scmReminderDialog[locale]) {
                this.state.data.scmReminderDialog[locale] = {};
              }

              // Store all properties
              this.state.data.scmReminderDialog[locale] = {
                // Request Updates Dialog
                requestUpdatesTitle: node.requestUpdatesTitle,
                requestUpdatesDescription: node.requestUpdatesDescription,
                requestUpdatesMessage: node.requestUpdatesMessage,
                requestUpdatesSendButtonText: node.requestUpdatesSendButtonText,

                // Request Submission Dialog
                requestSubmissionTitle: node.requestSubmissionTitle,
                requestSubmissionDescription: node.requestSubmissionDescription,
                requestSubmissionMessage: node.requestSubmissionMessage,
                requestSubmissionSendButtonText: node.requestSubmissionSendButtonText,

                // Common Dialog Elements
                cancelButtonText: node.cancelButtonText,

                // Status Messages and Dynamic Content
                suppliersSubmittedTemplate: node.suppliersSubmittedTemplate,
                suppliersWillReceiveTemplate: node.suppliersWillReceiveTemplate,
                lastReminderSentTemplate: node.lastReminderSentTemplate,

                // Processing States
                sendingRemindersTemplate: node.sendingRemindersTemplate,
                remindersSentSuccessMessage: node.remindersSentSuccessMessage,

                locale: node.locale
              };
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded SCM Reminder Dialog content for locales:',
                Object.keys(this.state.data.scmReminderDialog));
            }
            contentFound = true;
          }

          // Check for SCM Export content
          if (queryResult?.data?.allContentScmExport?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentScmExport.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.scmExport[locale]) {
                this.state.data.scmExport[locale] = {};
              }

              // Copy all fields directly from the node
              for (const key in node) {
                // Skip internal Gatsby fields
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  this.state.data.scmExport[locale][key] = node[key];
                }
              }
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded SCM Export content for locales:',
                Object.keys(this.state.data.scmExport));
            }
            contentFound = true;
          }

          // Check for allContentSa
          if (queryResult?.data?.allContentSa?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSa.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.allContentSa[locale]) {
                this.state.data.allContentSa[locale] = {};
              }

              // Copy all fields directly from the node - make sure this approach is used
              // instead of manually listing fields
              for (const key in node) {
                // Skip internal Gatsby fields
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  this.state.data.allContentSa[locale][key] = node[key];
                }
              }
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded SA content for locales:',
                Object.keys(this.state.data.allContentSa));
            }
            contentFound = true;
          }

          //  Self Assessment Business Segments content
          if (queryResult?.data?.allContentSaBs?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSaBs.nodes;

            nodes.forEach(n => {
              // Create locale entry if it doesn't exist
              if (!this.state.data.selfAssessmentBS[n.locale]) {
                this.state.data.selfAssessmentBS[n.locale] = {};
              }

              // Set all fields for this locale - including new pagePopoverContent
              this.state.data.selfAssessmentBS[n.locale] = {
                pageTitle: n.pageTitle,
                pageSubtitle: n.pageSubtitle,
                pagePopoverContent: n.pagePopoverContent,
                overviewButtonText: n.overviewButtonText,
                importButtonText: n.importButtonText,
                questionnaireTitle: n.questionnaireTitle,
                questionnaireSubtitle: n.questionnaireSubtitle,
                mapViewLabel: n.mapViewLabel,
                listViewLabel: n.listViewLabel,
                notStartedLabel: n.notStartedLabel,
                inProgressLabel: n.inProgressLabel,
                completeLabel: n.completeLabel,
                loadingCityViewText: n.loadingCityViewText,
                importingDataText: n.importingDataText,
                answeredLabel: n.answeredLabel,
                downloadQuestionnaireText: n.downloadQuestionnaireText
              };
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Self Assessment Business Segments content for locales:',
                Object.keys(this.state.data.selfAssessmentBS));
            }
          }

          // In preloadFromGraphQL method, add handling for the new content type:
          if (queryResult?.data?.allContentSaBsSection?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSaBsSection.nodes;

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.selfAssessmentBSSection[locale]) {
                this.state.data.selfAssessmentBSSection[locale] = {};
              }

              // Store all properties
              this.state.data.selfAssessmentBSSection[locale] = {
                energyEfficiencyTitle: node.energyEfficiencyTitle,
                logisticsTitle: node.logisticsTitle,
                renewableEnergyTitle: node.renewableEnergyTitle,
                resourceIntensityTitle: node.resourceIntensityTitle,
                processEmissionsTitle: node.processEmissionsTitle,
                emergingTechnologiesTitle: node.emergingTechnologiesTitle,
                inputsTitle: node.inputsTitle,
                hotspotsTitle: node.hotspotsTitle,
                businessTravelTitle: node.businessTravelTitle,
                locale: node.locale
              };
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Self Assessment Business Segments Sections content for locales:',
                Object.keys(this.state.data.selfAssessmentBSSection));
            }
            contentFound = true;
          }

          if (queryResult?.data?.allContentSaBsQuestion?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSaBsQuestion.nodes;

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.selfAssessmentBSQuestions[locale]) {
                this.state.data.selfAssessmentBSQuestions[locale] = {};
              }

              // Store all question text and help text properties
              // This copies all fields from the node to our state
              for (const key in node) {
                // Skip internal Gatsby fields and empty values
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key) &&
                  node[key] !== undefined && node[key] !== null) {
                  this.state.data.selfAssessmentBSQuestions[locale][key] = node[key];
                }
              }
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Self Assessment Business Segments Questions content for locales:',
                Object.keys(this.state.data.selfAssessmentBSQuestions));
            }
            contentFound = true;
          }

          if (queryResult?.data?.allContentSaCo?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSaCo.nodes;

            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.selfAssessmentCO[locale]) {
                this.state.data.selfAssessmentCO[locale] = {};
              }

              // Copy all fields directly from the node - instead of manually listing fields
              for (const key in node) {
                // Skip internal Gatsby fields
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  this.state.data.selfAssessmentCO[locale][key] = node[key];
                }
              }
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Self Assessment Company Overview content for locales:',
                Object.keys(this.state.data.selfAssessmentCO));
            }
            contentFound = true;
          }


          // In preloadFromGraphQL method, add handling for the new content type:
          if (queryResult?.data?.allContentSaBsElearning?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSaBsElearning.nodes;

            // Group eLearning content by locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale array if needed
              if (!this.state.data.selfAssessmentBSELearning[locale]) {
                this.state.data.selfAssessmentBSELearning[locale] = [];
              }

              // Add this eLearning item to the array
              this.state.data.selfAssessmentBSELearning[locale].push({
                sectionId: node.sectionId,
                title: node.title,
                content: node.content,
                locale: node.locale
              });
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Self Assessment Business Segments eLearning content for locales:',
                Object.keys(this.state.data.selfAssessmentBSELearning));
            }
            contentFound = true;
          }

          // Check for Customer Requests content
          if (queryResult?.data?.allContentCr?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentCr.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.customerRequests[locale]) {
                this.state.data.customerRequests[locale] = {};
              }

              // Store all properties - including new pagePopoverContent
              this.state.data.customerRequests[locale] = {
                pageTitle: node.pageTitle,
                pageSubtitle: node.pageSubtitle,
                pagePopoverContent: node.pagePopoverContent,
                submitButtonText: node.submitButtonText,
                headerCustomer: node.headerCustomer,
                headerSupplierId: node.headerSupplierId,
                headerProductGroup: node.headerProductGroup,
                headerLastRequest: node.headerLastRequest,
                headerStatus: node.headerStatus,
                statusAccept: node.statusAccept,
                statusAccepted: node.statusAccepted,
                statusDeclined: node.statusDeclined,
                unsubmittedChangesText: node.unsubmittedChangesText,
                lastSubmissionText: node.lastSubmissionText,
                updateRequestedBadge: node.updateRequestedBadge,
                invitationDateLabel: node.invitationDateLabel,
                declineRequestAction: node.declineRequestAction,
                noRequestsError: node.noRequestsError,
                noRequestsErrorTitle: node.noRequestsErrorTitle,
                acceptErrorMessage: node.acceptErrorMessage,
                declineErrorMessage: node.declineErrorMessage,
                locale: node.locale
              };
            });

            contentFound = true;
          }

          // Check for Sidebar content
          if (queryResult?.data?.allContentSidebar?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSidebar.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.sidebar[locale]) {
                this.state.data.sidebar[locale] = {};
              }

              // Store all properties
              this.state.data.sidebar[locale] = {
                overviewLabel: node.overviewLabel,
                selfAssessmentLabel: node.selfAssessmentLabel,
                carbonAccountingLabel: node.carbonAccountingLabel,
                supplyChainManagementLabel: node.supplyChainManagementLabel,
                helpLabel: node.helpLabel,
                settingsLabel: node.settingsLabel,
                companyProfileLabel: node.companyProfileLabel,
                userManagementLabel: node.userManagementLabel,
                signOutLabel: node.signOutLabel,
                privacyPolicyLabel: node.privacyPolicyLabel,
                termsLabel: node.termsLabel,
                changelogLabel: node.changelogLabel,
                aboutLabel: node.aboutLabel,
                companySettingsLabel: node.companySettingsLabel,
                accountLabel: node.accountLabel,
                locale: node.locale
              };
            });

            contentFound = true;
          }

          // Check for Submission Drawer content
          if (queryResult?.data?.allContentSubmission?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSubmission.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.submission[locale]) {
                this.state.data.submission[locale] = {};
              }

              // Copy all fields directly from the node
              for (const key in node) {
                // Skip internal Gatsby fields
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  this.state.data.submission[locale][key] = node[key];
                }
              }
            });

            contentFound = true;
          }

          // Check for User Management content
          if (queryResult?.data?.allContentUsers?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentUsers.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.users[locale]) {
                this.state.data.users[locale] = {};
              }

              // Store all properties - copy all node properties except internal Gatsby fields
              const contentFields = {};
              for (const key in node) {
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  contentFields[key] = node[key];
                }
              }

              this.state.data.users[locale] = contentFields;
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded User Management content for locales:',
                Object.keys(this.state.data.users));
            }
            contentFound = true;
          }

          // Check for Account Settings content
          if (queryResult?.data?.allContentSettings?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentSettings.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.settings[locale]) {
                this.state.data.settings[locale] = {};
              }

              // Store all properties - copy all node properties except internal Gatsby fields
              const contentFields = {};
              for (const key in node) {
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  contentFields[key] = node[key];
                }
              }

              this.state.data.settings[locale] = contentFields;
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Account Settings content for locales:',
                Object.keys(this.state.data.settings));
            }
            contentFound = true;
          }

          // Check for Company Profile content
          if (queryResult?.data?.allContentCompanyProfile?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentCompanyProfile.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.companyProfile[locale]) {
                this.state.data.companyProfile[locale] = {};
              }

              // Store all properties - copy all node properties except internal Gatsby fields
              const contentFields = {};
              for (const key in node) {
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  contentFields[key] = node[key];
                }
              }

              this.state.data.companyProfile[locale] = contentFields;
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Company Profile content for locales:',
                Object.keys(this.state.data.companyProfile));
            }
            contentFound = true;
          }

          // Check for IndustryList content
          if (queryResult?.data?.allContentIndustryList?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentIndustryList.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.industryList[locale]) {
                this.state.data.industryList[locale] = {};
              }

              // Store all properties - copy all node properties except internal Gatsby fields
              const contentFields = {};
              for (const key in node) {
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  contentFields[key] = node[key];
                }
              }

              this.state.data.industryList[locale] = contentFields;
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded IndustryList content for locales:',
                Object.keys(this.state.data.industryList));
            }
            contentFound = true;
          }

          // Check for Registration content
          if (queryResult?.data?.allContentRegistration?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentRegistration.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.registration[locale]) {
                this.state.data.registration[locale] = {};
              }

              // Store all properties - copy all node properties except internal Gatsby fields
              const contentFields = {};
              for (const key in node) {
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  contentFields[key] = node[key];
                }
              }

              this.state.data.registration[locale] = contentFields;
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Registration content for locales:',
                Object.keys(this.state.data.registration));
            }
            contentFound = true;
          }

          // Check for Invitation content
          if (queryResult?.data?.allContentInvitation?.nodes?.length > 0) {
            const nodes = queryResult.data.allContentInvitation.nodes;

            // Process each locale
            nodes.forEach(node => {
              const locale = node.locale || 'en';

              // Initialize locale object if needed
              if (!this.state.data.invitation[locale]) {
                this.state.data.invitation[locale] = {};
              }

              // Store all properties - copy all node properties except internal Gatsby fields
              const contentFields = {};
              for (const key in node) {
                if (!['id', 'internal', 'children', 'parent', '__typename'].includes(key)) {
                  contentFields[key] = node[key];
                }
              }

              this.state.data.invitation[locale] = contentFields;
            });

            if (process.env.NODE_ENV === 'development') {
              console.log('Preloaded Invitation content for locales:',
                Object.keys(this.state.data.invitation));
            }
            contentFound = true;
          }

        }

        if (contentFound) {
          if (process.env.NODE_ENV === 'development') {
            console.log('Content preloaded from GraphQL layer');
          }
          this.state.initialized = true;
          return true;
        }
      }

      return false;
    } catch (error) {
      console.warn('Error preloading content from GraphQL:', error);
      return false;
    }
  }
}